import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, GraduationCap, ArrowRight, 
  CheckCircle, Loader2, BookOpen, Calculator, FlaskConical,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('loading'); // loading, info, assessment, completed, error
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    child_year_group: ''
  });
  
  // Assessment state
  const [questions, setQuestions] = useState(null);
  const [currentSubject, setCurrentSubject] = useState('maths');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [childName, setChildName] = useState('');

  useEffect(() => {
    checkAssessmentStatus();
  }, [token]);

  const checkAssessmentStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment/${token}`);
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || 'Assessment not found');
        setStep('error');
        return;
      }
      
      if (data.status === 'completed') {
        setStep('completed');
      } else {
        setStep('info');
      }
    } catch (err) {
      console.error('Assessment status check error:', err);
      setError('Failed to load assessment');
      setStep('error');
    }
  };

  const handleStartAssessment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/assessment/${token}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || 'Failed to start assessment');
        setLoading(false);
        return;
      }
      
      setQuestions(data.questions);
      setChildName(data.child_name);
      setStep('assessment');
    } catch (err) {
      setError('Failed to start assessment');
    }
    setLoading(false);
  };

  const handleAnswer = (questionId, answerIndex) => {
    const key = `${currentSubject}_${questionId}`;
    setAnswers(prev => {
      const updated = { ...prev, [key]: answerIndex };
      // Backup to localStorage in case of submit failure
      try { localStorage.setItem(`assessment_${token}_answers`, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  const nextQuestion = () => {
    const subjects = ['maths', 'english', 'science'];
    const currentSubjectQuestions = questions[currentSubject];
    
    if (currentQuestion < currentSubjectQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Move to next subject
      const currentIndex = subjects.indexOf(currentSubject);
      if (currentIndex < subjects.length - 1) {
        setCurrentSubject(subjects[currentIndex + 1]);
        setCurrentQuestion(0);
      } else {
        // All done - submit
        submitAssessment();
      }
    }
  };

  const submitAssessment = async (retryCount = 0) => {
    setLoading(true);
    setError('');
    
    // Use localStorage backup if state answers are empty
    let submitAnswers = answers;
    if (Object.keys(submitAnswers).length === 0) {
      try {
        const backup = localStorage.getItem(`assessment_${token}_answers`);
        if (backup) submitAnswers = JSON.parse(backup);
      } catch {}
    }
    
    const url = `${API_URL}/api/assessment/${token}/submit`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: submitAnswers }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (res.ok) {
        try { localStorage.removeItem(`assessment_${token}_answers`); } catch {}
        setStep('completed');
        setLoading(false);
        return;
      }
      
      // Non-OK response
      let errorMsg = `Server error (${res.status})`;
      try {
        const data = await res.json();
        errorMsg = data.detail || errorMsg;
      } catch {}
      
      if (errorMsg.toLowerCase().includes('already') && errorMsg.toLowerCase().includes('completed')) {
        try { localStorage.removeItem(`assessment_${token}_answers`); } catch {}
        setStep('completed');
        setLoading(false);
        return;
      }
      
      // Auto-retry once on 500 errors
      if (res.status >= 500 && retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000));
        setLoading(false);
        return submitAssessment(retryCount + 1);
      }
      
      setError(errorMsg);
    } catch (err) {
      console.error('Submit error:', err.name, err.message);
      
      // Auto-retry on network/timeout errors
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000));
        setLoading(false);
        return submitAssessment(retryCount + 1);
      }
      
      // Final fallback: try XMLHttpRequest
      try {
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.timeout = 30000;
          xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.ontimeout = () => reject(new Error('Timeout'));
          xhr.send(JSON.stringify({ answers: submitAnswers }));
        });
        
        if (result.status >= 200 && result.status < 300) {
          try { localStorage.removeItem(`assessment_${token}_answers`); } catch {}
          setStep('completed');
          setLoading(false);
          return;
        }
      } catch (xhrErr) {
        console.error('XHR fallback also failed:', xhrErr);
      }
      
      setError('Unable to submit your assessment. Your answers have been saved — please press Try Again.');
    }
    setLoading(false);
  };

  const getProgress = () => {
    const subjects = ['maths', 'english', 'science'];
    const currentSubjectIndex = subjects.indexOf(currentSubject);
    const questionsPerSubject = 10;
    const completed = (currentSubjectIndex * questionsPerSubject) + currentQuestion;
    const total = 30;
    return Math.round((completed / total) * 100);
  };

  const currentQ = questions?.[currentSubject]?.[currentQuestion];
  const currentAnswerKey = currentQ ? `${currentSubject}_${currentQ.id}` : null;

  // Render based on step
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Assessment Not Available</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (step === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Assessment Complete!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for completing the assessment. Our team will review the results and contact you shortly to discuss your child's learning journey.
          </p>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-emerald-800 font-medium">What happens next?</p>
            <p className="text-emerald-700 text-sm mt-1">
              A member of our team will call you within 24-48 hours to discuss the detailed report and recommend the best learning path.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'info') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-slate-800">Growing Minds</h1>
            </div>
            <h2 className="text-xl text-slate-600">Initial Assessment</h2>
          </div>

          {/* Info Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">About This Assessment</h3>
              <p className="text-slate-600 text-sm">
                This short assessment helps us understand your child's current level in Maths, English, and Science. 
                It takes approximately 15-20 minutes to complete and will help us recommend the best starting point 
                for their learning journey.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <Calculator className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="font-semibold text-slate-800">Maths</p>
                <p className="text-xs text-slate-500">10 questions</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-4 text-center">
                <BookOpen className="w-8 h-8 text-rose-600 mx-auto mb-2" />
                <p className="font-semibold text-slate-800">English</p>
                <p className="text-xs text-slate-500">10 questions</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <FlaskConical className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-slate-800">Science</p>
                <p className="text-xs text-slate-500">10 questions</p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleStartAssessment}>
              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-slate-800">Parent/Guardian Details</h4>
                
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Your Full Name"
                    value={formData.parent_name}
                    onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.parent_email}
                    onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.parent_phone}
                    onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-slate-800">Child's Details</h4>
                
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Child's Name"
                    value={formData.child_name}
                    onChange={(e) => setFormData({ ...formData, child_name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Current Year Group</label>
                  <select
                    value={formData.child_year_group}
                    onChange={(e) => setFormData({ ...formData, child_year_group: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-slate-800 text-base"
                    required
                    data-testid="year-group-select"
                  >
                    <option value="" disabled>Select Year Group</option>
                    <option value="1">Year 1</option>
                    <option value="2">Year 2</option>
                    <option value="3">Year 3</option>
                    <option value="4">Year 4</option>
                    <option value="5">Year 5</option>
                    <option value="6">Year 6</option>
                    <option value="7">Year 7</option>
                    <option value="8">Year 8</option>
                    <option value="9">Year 9</option>
                    <option value="10">Year 10</option>
                    <option value="11">Year 11</option>
                  </select>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Start Assessment
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Assessment step
  if (step === 'assessment' && questions) {
    const subjectColors = {
      maths: { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600', icon: Calculator },
      english: { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600', icon: BookOpen },
      science: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', icon: FlaskConical }
    };
    
    const colors = subjectColors[currentSubject];
    const SubjectIcon = colors.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">
                {childName}'s Assessment
              </span>
              <span className="text-sm font-medium text-slate-600">
                {getProgress()}% Complete
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgress()}%` }}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-4 text-sm" data-testid="assessment-error">
              <p className="font-semibold mb-1">Submission Error</p>
              <p>{error}</p>
              <button 
                onClick={submitAssessment}
                className="mt-2 bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Subject Indicator */}
          <div className={`${colors.light} rounded-xl p-4 mb-6 flex items-center gap-4`}>

            <div className={`${colors.bg} w-12 h-12 rounded-lg flex items-center justify-center`}>
              <SubjectIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-slate-800 capitalize">{currentSubject}</h2>
              <p className="text-sm text-slate-600">Question {currentQuestion + 1} of 10</p>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-slate-800">
                {currentQ?.question}
              </h3>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-8">
              {currentQ?.options.map((option, index) => {
                const isSelected = answers[currentAnswerKey] === index;
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswer(currentQ.id, index)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? `${colors.bg} text-white border-transparent` 
                        : 'border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                      isSelected ? 'bg-white/20' : 'bg-slate-100'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>

            {/* Navigation */}
            <Button
              onClick={nextQuestion}
              disabled={answers[currentAnswerKey] === undefined || loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {currentSubject === 'science' && currentQuestion === 9 ? 'Submit Assessment' : 'Next Question'}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PublicAssessment;
